import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DropdownMenu } from 'radix-ui';
import { useTranslation } from 'react-i18next';

import { isLocale, SUPPORTED_LOCALES, type Locale } from '@emr/shared';

import { getOwnLocale, updateOwnLocale } from '../api/locale.ts';
import { DROPDOWN_MENU_ITEM_CLASS as ITEM_CLASS } from '../components/menuStyles.ts';
import { applyLocale } from './index.ts';
import { LOCALE_QUERY_KEY } from './useSyncAccountLocale.ts';

/** Radix `RadioGroup.value` must be a non-empty string, and `null` (the wire value for "no
 * preference") is not one — matching `ThemeMenu.tsx`'s own `'system'` sentinel for exactly this
 * shape of problem on the colour-mode radio group. */
const BROWSER_DEFAULT = 'browser-default';

const LOCALE_LABEL_KEY: Record<Locale, string> = {
  en: 'language.english',
  es: 'language.spanish',
};

/**
 * The language picker — third menu in this row, same shape as `ThemeMenu.tsx`/
 * `DateTimeFormatMenu.tsx` (`menuStyles.ts`'s own header already anticipated this).
 *
 * No contrast-guard/rollback the way `ThemeMenu` needs for a colour choice: a language pick has no
 * equivalent runtime check that can fail, so this is the simpler read/write shape
 * `SignaturePage.tsx`'s own settings query+mutation already uses.
 */
export function LanguageMenu() {
  const { t } = useTranslation('shell');
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: LOCALE_QUERY_KEY,
    queryFn: ({ signal }) => getOwnLocale(signal),
  });
  const currentLocale = query.data?.locale ?? null;

  const save = useMutation({
    mutationFn: (locale: Locale | null) => updateOwnLocale(locale),
    onSuccess: (data) => {
      queryClient.setQueryData(LOCALE_QUERY_KEY, data);
      // Applied immediately rather than waiting for `useSyncAccountLocale`'s own effect to notice
      // the cache write — the whole point of a picker is that it feels instant.
      applyLocale(data.locale);
    },
  });

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger
        className="inline-flex items-center gap-2 rounded-control border border-line bg-surface px-3 py-2 text-sm font-medium text-ink hover:bg-raised"
        aria-label={`${t('language.trigger')}: ${
          currentLocale === null ? t('language.browserDefault') : t(LOCALE_LABEL_KEY[currentLocale])
        }`}
      >
        {t('language.trigger')}
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          sideOffset={6}
          align="end"
          className="z-50 min-w-48 rounded-panel border border-line bg-surface p-1 text-ink shadow-overlay"
        >
          <DropdownMenu.RadioGroup
            value={currentLocale ?? BROWSER_DEFAULT}
            onValueChange={(value) => {
              if (value === BROWSER_DEFAULT) {
                save.mutate(null);
              } else if (isLocale(value)) {
                save.mutate(value);
              }
            }}
          >
            <DropdownMenu.RadioItem value={BROWSER_DEFAULT} className={ITEM_CLASS}>
              <span aria-hidden="true" className="w-4 text-accent-text">
                <DropdownMenu.ItemIndicator>✓</DropdownMenu.ItemIndicator>
              </span>
              {t('language.browserDefault')}
            </DropdownMenu.RadioItem>
            {SUPPORTED_LOCALES.map((locale) => (
              <DropdownMenu.RadioItem key={locale} value={locale} className={ITEM_CLASS}>
                <span aria-hidden="true" className="w-4 text-accent-text">
                  <DropdownMenu.ItemIndicator>✓</DropdownMenu.ItemIndicator>
                </span>
                {t(LOCALE_LABEL_KEY[locale])}
              </DropdownMenu.RadioItem>
            ))}
          </DropdownMenu.RadioGroup>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
